import type { Glossary } from "./types";

export const fr = {
  common: {
    appName: "Miaparle",
    home: "Accueil",
    continue: "Continuer",
    save: "Enregistrer",
    next: "Suivant →",
    clickToStart: "Cliquez ici pour commencer",
    goodJob: "Bravo !",
    notQuite: "Bel essai !",
    tryAgain: "Réessayer",
    exerciseComplete: "Exercice terminé !",
    bestScore: (score, max) => `Meilleur : ${score}/${max}`,
    newBestScore: "Nouveau meilleur score !",
    wordProgress: (current, total) => `Mot ${current} sur ${total}`,
    closeHelp: "Fermer l'aide",
    firefoxWarning:
      "Il semble que vous utilisez Firefox. Pour une meilleure expérience, nous recommandons Chrome, Edge ou Safari, car certaines fonctionnalités peuvent ne pas fonctionner correctement dans Firefox.",
    scoreFeedbackFantastic: (score, max) =>
      `Votre score : ${score}/${max}. Fantastique !`,
    scoreFeedbackGreat: (score, max) =>
      `Votre score : ${score}/${max}. Excellent !`,
    scoreFeedbackWellDone: (score, max) =>
      `Votre score : ${score}/${max}. Bien joué !`,
    scoreFeedbackCanDoBetter: (score, max) =>
      `Votre score : ${score}/${max}. Vous pouvez faire mieux !`,
  },
  home: {
    welcome: (name: string) => `Bienvenue, ${name} !`,
    letsSpeakTitle: "Parlons !",
    tuneYourEarTitle: "Entraînons notre oreille !",
    challengeYourEarTitle: "Défions notre oreille !",
    progress: (current, total) => `Progression ${current}/${total}`,
    score: (score, total) => `Score ${score}/${total}`,
    summaryLink: "Voir votre résumé →",
  },
  summary: {
    title: "Bravo !",
    message:
      "Vous avez terminé tous les exercices\n\nVous pouvez les refaire pour vous entraîner davantage !",
    speak: "Parlons !",
    tune: "Entraînons notre oreille !",
    challenge: "Défions notre oreille !",
  },
  onboarding: {
    welcome: "Bienvenue !",
    yourName: "Votre nom",
    enterYourName: "Entrez votre nom",
    nameReserved: "Ce nom est réservé, veuillez en choisir un autre",
    allowRecordings: "Autoriser l'enregistrement des données pour la recherche",
    firefoxAck: "Je comprends et souhaite continuer quand même",
    firefoxAckRequired: "Veuillez accepter l'avertissement Firefox",
  },
  notFound: {
    title: "Page introuvable",
    description: "Cette page n'existe pas.",
  },
  settings: {
    title: "Paramètres",
    successSound: "Jouer un son de réussite",
    resetDescription: "Réinitialiser toute la progression et les paramètres",
    resetButton: "Réinitialiser l'application",
  },
  prod: {
    title: "Parlons !",
    instruction: "Dites le mot à voix haute",
    playWord: "▷ Écouter le mot",
    playWordLabel: "Écouter le mot",
    showHintsLabel: "Afficher les indices",
    hideHintsLabel: "Masquer les indices",
    turnToHints: "Retourner la carte vers les indices",
    turnToImage: "Retourner la carte vers l'image",
    playMyAttempt: "Écouter mon essai",
    starting: "Démarrage...",
    recording: "Écoute...",
    analyzing: "Analyse...",
    speak: "Parler",
    listenToWordAndTryAgain: "Bel essai ! Écoutez le mot et réessayez !",
    checkHintsAndTryAgain: "Bel essai ! Regardez les indices et réessayez !",
    youllDoBetterNext: "Bel essai ! Vous ferez mieux la prochaine fois !",
    nextWord: "Mot suivant →",
    skipPrompt:
      "Quelque chose n'a pas fonctionné plusieurs fois de suite. Voulez-vous passer l'exercice ?",
    skipExercise: "Passer l'exercice",
    keepTrying: "Continuer",
    tuneYourEarPrompt:
      "Entraînons notre oreille pour améliorer votre prononciation !",
    tuneYourEarLink: "Entraînons notre oreille ! →",
    speakAgain: "Parlez à nouveau",
    consentTitle:
      "Autorisez-vous l'utilisation de vos enregistrements vocaux pour améliorer notre application ?",
    consentSubtitle:
      "Vous pouvez toujours modifier ce choix dans les paramètres",
    consentYes: "Oui",
    consentNo: "Non",
    help: {
      step1:
        "Cliquez sur parler et dites le mot. L'enregistrement s'arrête automatiquement",
      step2: "Cliquez sur 'écouter le mot' pour entendre le mot",
      step3:
        "Cliquez sur 'afficher les indices' pour obtenir des informations sur le mot",
    },
  },
  train: {
    title: "Entraînons notre oreille !",
    instruction:
      "Quelle forme correspond au schéma accentuel du mot que vous entendez ?",
    clickShapeToAnswer: "Cliquez sur une forme pour répondre",
    replayWord: "▷ Réécouter le mot",
    playing: "Lecture en cours",
    nextWord: "Mot suivant →",
    challengeYourEarLink: "Défions notre oreille ! →",
    help: {
      step1:
        "Cliquez sur 'réécouter le mot' pour entendre le mot à nouveau, si nécessaire",
      step2:
        "Cliquez sur la forme qui correspond au schéma accentuel que vous entendez",
      step3: "Chaque forme montre un schéma accentuel différent",
    },
  },
  omo: {
    title: "Défions notre oreille !",
    instruction: "Quel mot est accentué différemment ?",
    clickNumberToAnswer: "Cliquez sur 1, 2 ou 3 pour répondre",
    replayWords: "▷ Réécouter les mots",
    playing: "Lecture en cours",
    nextWord: "Mot suivant →",
    challengeAgain: "Défions notre oreille encore !",
    help: {
      step1: "Cliquez sur 'réécouter les mots' pour entendre 3 mots",
      step2:
        "Cliquez sur 1, 2 ou 3 pour sélectionner le mot qui est accentué différemment",
    },
  },
  errors: {
    allowMicrophone: "Autoriser le microphone",
    couldNotProcessAudio:
      "Impossible de traiter l'audio. Essayez d'enregistrer à nouveau",
    couldNotAnalyzeSpeech:
      "Impossible d'analyser la parole. Essayez de parler plus clairement",
    recordingFailed: "Échec de l'enregistrement. Veuillez réessayer",
    somethingWentWrong: "Une erreur s'est produite. Veuillez réessayer",
    microphoneInstructions:
      "Pour activer : cliquez sur le menu ⋮ → Paramètres → Paramètres du site → Microphone → trouvez\nce site → Autoriser",
  },
} as const satisfies Glossary;
